import React, { useEffect, useState, memo } from 'react';
import { Box, Container, Typography, Fab, Chip, Button } from '@mui/material';
import { ZoomIn, ShoppingCart, Email, KeyboardArrowUp } from '@mui/icons-material';
import SearchFilters from '../SearchFilters';
import analyticsService from '../../services/analyticsService';
import { useNavigate } from 'react-router-dom';

import { useTableaux } from '../../hooks/useTableauxQueries';
import { useGalleryStore } from '../../stores/useGalleryStore';
import { webpVariantUrl } from '../../services/imageService';

const STATUS_COLOR = { vendu: '#e53935', réservé: '#fb8c00', reserve: '#fb8c00', indisponible: '#fb8c00' };
const STATUS_LABEL = { vendu: 'Vendu', réservé: 'Réservé', reserve: 'Réservé', indisponible: 'Indisponible' };

const TableauGridCard = memo(({ tableau, onClick }) => {
  const statusColor = STATUS_COLOR[tableau.disponibilite];
  const statusLabel = STATUS_LABEL[tableau.disponibilite];
  const isUnavailable = !!statusColor;

  return (
    <Box
      onClick={() => onClick(tableau)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(tableau); } }}
      sx={{
        cursor: 'pointer',
        display: 'block',
        breakInside: 'avoid',
        mb: 2,
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: '#fff',
        border: '1px solid #e8e8e8',
        transition: 'box-shadow 0.25s ease, transform 0.25s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
          '& .zoom-overlay': { opacity: 1 },
        },
        '&:focus-visible': { outline: '2px solid #c9a96e', outlineOffset: 2 },
      }}
    >
      {/* Image */}
      <Box sx={{
        position: 'relative',
        width: '100%',
        height: 195,
        bgcolor: '#f7f6f2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}>
        <Box
          component="img"
          loading="lazy"
          decoding="async"
          // Sert la variante WebP légère (~-94% de poids). Si elle n'existe pas
          // sur le disque (original plus petit que 480px), onError retombe sur
          // l'image d'origine — donc jamais d'image cassée.
          src={webpVariantUrl(tableau.image, 480) || tableau.image || '/placeholder-art.jpg'}
          onError={(e) => {
            const img = e.currentTarget;
            if (img.dataset.fb) return;
            img.dataset.fb = '1';
            img.src = tableau.image || '/placeholder-art.jpg';
          }}
          alt={tableau.titre}
          sx={{
            maxWidth: '88%',
            maxHeight: '88%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            display: 'block',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.12))',
          }}
        />

        {/* Hover overlay */}
        <Box
          className="zoom-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: 'rgba(26,26,26,0.18)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.25s ease',
          }}
        >
          <ZoomIn sx={{ fontSize: 28, color: '#fff' }} />
        </Box>

        {/* Badge uniquement si non-disponible */}
        {isUnavailable && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: statusColor,
              color: '#fff',
              fontSize: '0.58rem',
              fontWeight: 700,
              px: 0.75,
              py: 0.3,
              borderRadius: '3px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {statusLabel}
          </Box>
        )}
      </Box>

      {/* Infos */}
      <Box sx={{ px: 1.5, py: 1.25, borderTop: '1px solid #f0f0f0' }}>
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: '0.8rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: '#1a1a1a',
            lineHeight: 1.3,
          }}
        >
          {tableau.titre}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 0.5 }}>
          <Typography sx={{ fontSize: '0.68rem', color: '#999', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, mr: 0.5 }}>
            {tableau.technique || ''}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#c9a96e', flexShrink: 0 }}>
            {typeof tableau.prix === 'number' ? `${tableau.prix} €` : tableau.prix}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});
TableauGridCard.displayName = 'TableauGridCard';

const SkeletonCard = () => (
  <Box sx={{ borderRadius: '8px', overflow: 'hidden', bgcolor: '#fff', border: '1px solid #e8e8e8', display: 'block', breakInside: 'avoid', mb: 2 }}>
    <Box sx={{
      height: 195,
      bgcolor: '#f0f0f0',
      '@keyframes pulse': { '0%,100%': { opacity: 0.5 }, '50%': { opacity: 1 } },
      animation: 'pulse 1.4s ease-in-out infinite',
    }} />
    <Box sx={{ px: 1.5, py: 1.25, borderTop: '1px solid #f0f0f0' }}>
      <Box sx={{ height: 13, bgcolor: '#ebebeb', borderRadius: 1, width: '65%', mb: 0.5 }} />
      <Box sx={{ height: 11, bgcolor: '#f3f3f3', borderRadius: 1, width: '45%' }} />
    </Box>
  </Box>
);

const GRID_SX = {
  columnCount: { xs: 1, sm: 2, md: 3, lg: 4 },
  columnGap: '16px',
};

const PAGE_SIZE = 20;

const MainGallerySection = ({ showHeader }) => {
  const { filters } = useGalleryStore();
  const { data: response = { data: [], total: 0 }, isLoading } = useTableaux(filters);
  const filteredTableaux = response.data;
  const navigate = useNavigate();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [filters]);

  const visibleTableaux = filteredTableaux.slice(0, visibleCount);
  const hasMore = visibleCount < filteredTableaux.length;

  const handleSelectTableau = (tableau) => {
    navigate(`/tableau/${tableau.id}`);
  };

  return (
    <>
      <Box component="main" className="main-content" sx={{ flex: 1, py: 5, bgcolor: '#faf8f5' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>

          {/* En-tête */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              component="p"
              sx={{
                fontSize: { xs: '0.68rem', md: '0.72rem' },
                fontWeight: 600,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                color: '#c9a96e',
                mb: 1.25,
              }}
            >
              La collection
            </Typography>
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontStyle: 'italic',
                fontWeight: 300,
                mb: 1.5,
                color: 'primary.main',
                fontSize: { xs: '2.1rem', md: '3rem' },
                lineHeight: 1.1,
              }}
            >
              Œuvres originales
            </Typography>
            {/* Filet or + losange */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mb: 1.5 }}>
              <Box sx={{ width: 40, height: '1px', bgcolor: '#c9a96e' }} />
              <Box sx={{ width: 5, height: 5, transform: 'rotate(45deg)', bgcolor: '#c9a96e' }} />
              <Box sx={{ width: 40, height: '1px', bgcolor: '#c9a96e' }} />
            </Box>
            <Typography variant="body1" color="text.secondary">
              Découvrez une sélection de pièces uniques signées Noëlle Boronad
            </Typography>
          </Box>

          {/* Modalités d'achat */}
          <Box
            sx={{
              mb: 3,
              p: { xs: 1.5, sm: 2 },
              border: '1px solid',
              borderColor: 'secondary.light',
              borderRadius: 2,
              bgcolor: 'rgba(201,169,110,0.06)',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
              <ShoppingCart sx={{ color: 'primary.main', fontSize: 22, flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">
                Aucune vente directe — contact obligatoire avec l'artiste — livraison selon accord
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<Email />}
              onClick={() => navigate('/contact-artist')}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                width: { xs: '100%', sm: 'auto' },
                flexShrink: 0,
              }}
            >
              Contacter l'artiste
            </Button>
          </Box>

          {/* Filtres */}
          <Box sx={{ mb: 3 }}>
            <SearchFilters />
          </Box>

          {/* Grille */}
          {isLoading ? (
            <Box sx={GRID_SX}>
              {Array.from({ length: 12 }).map((_, n) => <SkeletonCard key={n} />)}
            </Box>
          ) : filteredTableaux.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h5" color="text.secondary" gutterBottom>Aucun tableau trouvé</Typography>
              <Typography variant="body2" color="text.secondary">Essayez d'ajuster vos filtres de recherche</Typography>
            </Box>
          ) : (
            <>
              <Box sx={GRID_SX}>
                {visibleTableaux.map((tableau) => (
                  <TableauGridCard key={tableau.id} tableau={tableau} onClick={handleSelectTableau} />
                ))}
              </Box>

              <Box sx={{ textAlign: 'center', mt: 4, pt: 3, borderTop: '1px solid #e8e8e8' }}>
                <Typography variant="body2" color="text.secondary">
                  {visibleTableaux.length} / {filteredTableaux.length} {filteredTableaux.length > 1 ? 'œuvres' : 'œuvre'}
                </Typography>
                {hasMore && (
                  <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                    Voir plus ({filteredTableaux.length - visibleCount} restantes)
                  </Button>
                )}
              </Box>
            </>
          )}
        </Container>
      </Box>

      <Fab
        color="primary"
        size="medium"
        aria-label="Retour en haut de page"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          opacity: showHeader ? 0 : 1,
          pointerEvents: showHeader ? 'none' : 'auto',
          transition: 'opacity 0.3s',
          zIndex: 1000,
        }}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <KeyboardArrowUp />
      </Fab>

    </>
  );
};

export default MainGallerySection;
